/* eslint-disable ember/no-computed-properties-in-native-classes, warp-drive/no-legacy-request-patterns */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { union, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import type StoreService from '@linc-technologies/linced-common/services/store';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import ContactType from 'linced-app/enums/contact-type';
import ExpulsionOutcome from 'linced-app/enums/expulsion-outcome';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';
import type BehaviourIncident from 'linced-app/models/attendance/behaviour-incident';
import type Suspension from 'linced-app/models/attendance/suspension';
import type Media from 'linced-app/models/media';
import type Person from 'linced-app/models/person';
import type Post from 'linced-app/models/posts/post';
import type School from 'linced-app/models/school';
import type BehaviourDatesService from 'linced-app/services/behaviour-dates';

// Proto: attendance.Expulsion.
export default class Expulsion extends Model {
  declare [Type]: 'attendance/expulsion';

  @service
  declare behaviourDates: BehaviourDatesService;

  @service
  declare store: StoreService;

  // externalId is the expulsion's id in a 3rd party database
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // createTime is when the resource was created, in seconds from the epoch,
  // not directly displayed but may be used in record management.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified, in seconds from the epoch,
  // not directly displayed but may be used in record tracking.
  @attr('number')
  declare updateTime: number | undefined;

  // date is the date in YYYY-MM-DD format when this expulsion was created
  @attr('string')
  declare date: string | undefined;

  // schoolId corresponds to the identifier of the school managing the expulsion,
  // not directly displayed in the table.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // authorId is the identifier of the individual who recorded the expulsion,
  // not displayed in the table.
  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  // learnerId is the identifier of the student being expelled, used to link to the
  // Student details (Name, SRN, Year Level, Roll Class).
  @belongsTo('person', { async: true, inverse: null })
  declare learnerId: AsyncBelongsTo<Person>;

  @alias('learnerId')
  declare learner: AsyncBelongsTo<Person>;

  // incidentId is the identifier of the incident that led to the expulsion,
  // not displayed in the table.
  @belongsTo('attendance/behaviour-incident', {
    async: true,
    inverse: 'expulsionId',
  })
  declare incidentId: AsyncBelongsTo<BehaviourIncident>;

  @alias('incidentId')
  declare incident: AsyncBelongsTo<BehaviourIncident>;

  // stage is autoset to Written Notice of Potential Expulsion. (not modifiable)
  @attr('number', { defaultValue: 0 })
  declare stage: number;

  // noticeDate is the date when the written notice of potential expulsion was issued,
  // defaulting to the current date.
  @attr('string')
  declare noticeDate: string | undefined;

  // expulsionStartDate is the date when the expulsion starts; it must be a valid school day.
  @attr('string')
  declare expulsionStartDate: string | undefined;

  // reviewDate is the scheduled school day to review the expulsion decision.
  @attr('string')
  declare reviewDate: string | undefined;

  // caregiverMeetingDate is the date when a meeting with the learner's caregiver is scheduled.
  @attr('string')
  declare caregiverMeetingDate: string | undefined;

  // nominatedSchoolPersonId is the school representative responsible for the expulsion;
  // required for the letter template.
  @belongsTo('person', { async: true, inverse: null })
  declare nominatedSchoolPersonId: AsyncBelongsTo<Person>;

  @alias('nominatedSchoolPersonId')
  declare nominatedSchoolPerson: AsyncBelongsTo<Person>;

  // caregiverIds are the caregivers who will be contacted by the school regarding this expulsion.
  @hasMany('person', { async: true, inverse: null })
  declare caregiverIds: AsyncHasMany<Person>;

  @alias('caregiverIds')
  declare caregivers: AsyncHasMany<Person>;

  // title is the post title or short description of the expulsion record.
  @attr('string', { defaultValue: '' })
  declare title: string;

  // factualDetailsOfBehavioursOfConcern is the detailed description of the student's behaviors
  // leading to the expulsion, used in the DoE letter template and post content.
  @attr('string', { defaultValue: '' })
  declare factualDetailsOfBehavioursOfConcern: string;

  // specificDetailsOfDocuments refers to any documentation relevant to the expulsion,
  // used in the DoE letter template and post content.
  @attr('string', { defaultValue: '' })
  declare specificDetailsOfDocuments: string;

  // reasonForDecision refers to the reasoning for the decision on the outcome
  // of the expulsion,
  @attr('string', { defaultValue: '' })
  declare reasonForDecision: string;

  // attachments are documents related to the expulsion, such as reports or written statements,
  // which can be attached to the post.
  @hasMany('media', { inverse: null, async: true })
  declare attachments: AsyncHasMany<Media>;

  // expulsionPostId is the identifier of the post containing the base expulsion information.
  @belongsTo('posts/post', { async: true, inverse: null })
  declare expulsionPostId: AsyncBelongsTo<Post>;

  @alias('expulsionPostId')
  declare expulsionPost: AsyncBelongsTo<Post>;

  // noticeOfPotentialExpulsionId is the identifier of the post containing
  // the notice of potential expulsion.
  @hasMany('posts/post', { async: true, inverse: null })
  declare noticeOfPotentialExpulsionIds: AsyncHasMany<Post>;

  @alias('noticeOfPotentialExpulsionIds')
  declare noticeOfPotentialExpulsions: AsyncHasMany<Post>;

  // noticeOfExpulsionId is the identifier of the post containing the
  // notice of expulsion.
  @hasMany('posts/post', { async: true, inverse: null })
  declare noticeOfExpulsionIds: AsyncHasMany<Post>;

  @alias('noticeOfExpulsionIds')
  declare noticeOfExpulsions: AsyncHasMany<Post>;

  // allLetters is an array of all the letters generated by this expulsion
  // .content is the loaded ManyArray: the array macros ignore a
  // PromiseManyArray, which is not an array.
  @union('noticeOfPotentialExpulsions.content', 'noticeOfExpulsions.content')
  declare allLetters: unknown[];

  // suspensionStartDate is the date when the suspension starts.
  @attr('string')
  declare suspensionStartDate: string | undefined;

  // suspensionEndDate is the date when the suspension ends.
  @attr('string')
  declare suspensionEndDate: string | undefined;

  // suspensionExtensionDelApproval is true when the DEL has approved a suspension period longer than 15 days.
  @attr('boolean', { defaultValue: false })
  declare suspensionExtensionDelApproval: boolean;

  // outcome contains information about what happened after being expelled
  @attr('number', { defaultValue: 0 })
  declare outcome: number;

  // earlyReturnDate is the return date if the student returned early from suspension
  @attr('string')
  declare earlyReturnDate: string | undefined;

  // jurisdiction is the jurisdiction where this suspension was recorded
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // suspensionDuration is the duration of the associated suspension in school days
  @attr('number', { defaultValue: 0 })
  declare suspensionDuration: number;

  @alias('suspensionId')
  declare suspension: unknown;

  @belongsTo('attendance/suspension', { async: true, inverse: 'expulsionId' })
  declare suspensionId: AsyncBelongsTo<Suspension>;

  // displayStage is the human-readable label for the expulsion stage.
  @computed('stage')
  get displayStage() {
    return ExpulsionStage.getLabel(this.stage);
  }

  // displayOutcome is the human-readable label for the expulsion outcome.
  @computed('outcome')
  get displayOutcome() {
    return ExpulsionOutcome.getLabel(this.outcome);
  }

  @computed('learner.learner.contacts.[]', 'caregivers.content.[]')
  get learnerContacts() {
    const caregivers = resolved(this.caregivers);
    // TS-STANDIN(proto people.Contact): raw attr holds plain JSON.
    return (
      this.learner.get('learner.contacts') as {
        notes: string;
        type: number;
        caregiverId: string;
      }[]
    )
      .filter((contact) => {
        const notes = contact.notes.toLowerCase();
        if (notes === 'deceased') {
          return false;
        }
        if (notes === 'caseworker' && contact.type === ContactType.EMERGENCY) {
          return true;
        }
        return (
          contact.type === ContactType.PRIMARY ||
          contact.type === ContactType.SECONDARY
        );
      })
      .map((contact) => {
        return this.store.peekRecord<Person>('person', contact.caregiverId);
      })
      .filter((caregiver) => {
        // TS-BUG: peekRecord gives null for an unloaded caregiver, and the
        // null stays in the list.
        return !caregivers.includes(caregiver!);
      });
  }
}
